"use client"
import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import { Button } from "../ui/button"
import { Progress } from "../ui/progress"
import { BookOpen, Clock, Edit, LogOut } from "lucide-react"
import { useAuth } from '../../context/AuthContext'
import { formationAPI, API_BASE_URL } from '../../services/api'

// Type definitions for dynamic data
interface FormationProgress {
  id: number
  name: string
  image: string | null
  categoryName: string | null
  progress: string // e.g., "75%"
  lastUpdate: string | null
  status: string
}

interface ApiResponse {
  success: boolean
  data: FormationProgress[]
}

export default function DashboardUser() {
  const { user, logout } = useAuth()
  
  // State for dynamic formations data
  const [formations, setFormations] = useState<FormationProgress[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Static user data (as requested)
  const UserData = {
    name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || null,
    email: user?.email || null,
    avatar: user?.photo || null,
    joinedDate: user?.createdAt || null,
  };

  // Create a utility function at the top of your component or in a separate utils file
  const getImageUrl = (imagePath: string | null | undefined): string => {
    
    if (!imagePath || imagePath === "/placeholder.svg") {
      return "/placeholder.svg";
    }
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    // If it's a relative path, prepend the API base URL
    return `${API_BASE_URL}${imagePath}`;
  };

  // Fetch formations data on component mount
  useEffect(() => {
    if (user?.id) {
      fetchFormationProgress()
    }
  }, [user?.id])

  const fetchFormationProgress = async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (!user?.id) {
        setError('Utilisateur non connecté')
        return
      }

      const response = await formationAPI.getFormationProgress(user.id)
      
      if (response.data.success) {
        setFormations(response.data.data)
      } else {
        setError('Erreur lors du chargement des formations')
      }
    } catch (err) {
      console.error('Error fetching formation progress:', err)
      setError('Erreur lors du chargement des formations')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    window.location.href = '/signin'
  }

  // Calculate average progress from dynamic data
  const calculateAverageProgress = () => {
    if (formations.length === 0) return 0
    
    const totalProgress = formations.reduce((acc, formation) => {
      const progressValue = parseInt(formation.progress.replace('%', ''))
      return acc + progressValue
    }, 0)
    
    return Math.round(totalProgress / formations.length)
  }

  // Format date for display
  const formatDate = (date: string | Date | null) => {
    if (!date) return 'Jamais';

    try {
      const parsedDate = typeof date === 'string' ? new Date(date) : date;
      return parsedDate.toLocaleDateString("fr-FR");
    } catch {
      return 'Date invalide';
    }
  };

  // Get progress value as number
  const getProgressValue = (progressString: string) => {
    return parseInt(progressString.replace('%', ''))
  }

  return (
    <div className="flex-1">
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-serif font-bold tracking-tight">Tableau de bord</h1>
              <p className="text-gray-500 dark:text-gray-400">
                Bienvenue, {UserData.name}. Gérez vos informations et suivez vos formations.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" asChild>
                <a href="/dashboard/profile">
                  <Edit className="mr-2 h-4 w-4" />
                  Modifier le profil
                </a>
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </Button>
            </div>
          </div>

          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:w-auto">
              <TabsTrigger value="profile">Profil</TabsTrigger>
              <TabsTrigger value="courses">Mes formations</TabsTrigger>
            </TabsList>

            {/* Onglet Profil */}
            <TabsContent value="profile" className="mt-6">
              <div className="grid gap-6 md:grid-cols-2">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle>Informations personnelles</CardTitle>
                    <CardDescription>Vos informations de profil</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
                      <div className="relative w-32 h-32">
                        {UserData.avatar && UserData.avatar !== "/placeholder.svg" ? (
                          <img
                            src={getImageUrl(UserData.avatar)}
                            alt={UserData.name || undefined}
                            className="rounded-full object-cover border-4 border-white shadow-md w-full h-full absolute inset-0"
                            onError={(e) => {
                              // Fallback to placeholder if image fails to load
                              e.currentTarget.src = "/placeholder.svg";
                            }}
                          />
                        ) : (
                          <div className="flex items-center justify-center rounded-full bg-gray-300 text-white text-4xl font-bold border-4 border-white shadow-md w-full h-full absolute inset-0">
                            {UserData.name?.charAt(0).toUpperCase() || "U"}
                          </div>
                        )}
                      </div>
                      <div className="space-y-4 text-center md:text-left">
                        <div>
                          <h3 className="text-xl font-bold">{UserData.name}</h3>
                          <p className="text-gray-500 dark:text-gray-400">{UserData.email}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Membre depuis {formatDate(UserData.joinedDate)}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formations.length} formations suivies
                          </p>
                        </div>
                        <div className="flex justify-center md:justify-start gap-2">
                          <Button variant="outline" size="sm" asChild>
                            <a href="/dashboard/profile">
                              <Edit className="mr-2 h-4 w-4" />
                              Modifier
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle>Statistiques</CardTitle>
                    <CardDescription>Votre activité sur la plateforme</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium">Progression moyenne</span>
                          <span className="text-sm font-medium">
                            {calculateAverageProgress()}%
                          </span>
                        </div>
                        <Progress
                          value={calculateAverageProgress()}
                          className="h-2"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <BookOpen className="h-5 w-5 text-elille-blue" />
                            <span className="text-sm font-medium">Formations</span>
                          </div>
                          <p className="text-2xl font-bold">{formations.length}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="h-5 w-5 text-elille-red" />
                            <span className="text-sm font-medium">Heures</span>
                          </div>
                          <p className="text-2xl font-bold">24</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Onglet Mes formations - Dynamic */}
            <TabsContent value="courses" className="mt-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle>Mes formations</CardTitle>
                  <CardDescription>Les formations auxquelles vous êtes inscrit(e)</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
                      <p className="text-gray-500 dark:text-gray-400">Chargement de vos formations...</p>
                    </div>
                  ) : error ? (
                    <div className="text-center py-12">
                      <p className="text-red-500 mb-4">{error}</p>
                      <Button 
                        variant="outline" 
                        onClick={fetchFormationProgress}
                        size="sm"
                      >
                        Réessayer
                      </Button>
                    </div>
                  ) : formations.length === 0 ? (
                    <div className="text-center py-12">
                      <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-500 dark:text-gray-400 mb-4">
                        Vous n'êtes inscrit(e) à aucune formation pour le moment.
                      </p>
                      <Button variant="outline" asChild>
                        <a href="/formations">
                          Découvrir les formations
                        </a>
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {formations.map((formation) => (
                        <div key={formation.id} className="flex flex-col md:flex-row gap-4 border-b pb-6 last:border-0">
                          <div className="relative w-full md:w-48 h-32 flex-shrink-0">
                            <img
                              src={formation.image ? `${formation.image}` : "/placeholder.svg"}
                              alt={formation.name}
                              className="rounded-lg object-cover w-full h-full absolute inset-0"
                            />
                          </div>
                          <div className="flex-1 space-y-3">
                            <div>
                              {formation.categoryName && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 mb-2">
                                  {formation.categoryName}
                                </span>
                              )}
                              <h3 className="text-lg font-bold">{formation.name}</h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400">Par Elille academy</p>
                              
                              {/* Status badge */}
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  formation.status === 'Terminée' 
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : formation.status === 'En cours'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}>
                                  {formation.status}
                                </span>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span>Progression</span>
                                <span>{formation.progress}</span>
                              </div>
                              <Progress value={getProgressValue(formation.progress)} className="h-2" />
                            </div>
                            <div className="flex justify-between items-center">
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Dernier accès: {formatDate(formation.lastUpdate)}
                              </p>
                              <Button size="sm" asChild>
                                <a href={`/dashboard/watch/${formation.id}`}>
                                  {getProgressValue(formation.progress) === 0 ? 'Commencer' : 'Continuer'}
                                </a>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}